import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Dumbbell, Ruler, Package, ChevronRight, Flame, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

const fmtDate = (iso) => { if (!iso) return "—"; try { const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" }); } catch { return "—"; } };

export default function MemberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard/member").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="space-y-4"><Skeleton className="h-40 rounded-3xl bg-[#13131C]" /><div className="grid grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl bg-[#13131C]" />)}</div></div>;

  const { member, next_appointment, weekly_workouts } = data;
  const pct = member.total_lessons ? Math.round((member.remaining_lessons / member.total_lessons) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="member-dashboard">
      <div>
        <p className="text-sm text-[#68687B]">Hoş geldin 👋</p>
        <h1 className="font-display font-extrabold text-2xl text-white">{member.name?.split(" ")[0]}</h1>
      </div>

      {/* Next appointment hero */}
      {next_appointment ? (
        <button type="button" onClick={() => navigate("/member/appointments")}
          className="w-full text-left relative rounded-3xl bg-gradient-to-br from-[#FF5500] to-[#d94800] p-6 overflow-hidden hover:brightness-110 transition-[filter]" data-testid="member-next-appointment">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/80">Sıradaki Randevun</p>
          <p className="font-display font-black text-3xl text-white mt-1">{fmtDate(next_appointment.date)}, {next_appointment.time}</p>
          <p className="text-sm text-white/90 mt-1">{next_appointment.service}{next_appointment.trainer ? ` · ${next_appointment.trainer}` : ""}</p>
          <div className="flex items-center gap-2 mt-4 text-white/80 text-xs"><CalendarClock size={14} /> Randevularım sayfasından yönet <ChevronRight size={14} /></div>
        </button>
      ) : (
        <div className="relative rounded-3xl bg-[#13131C] border border-[#222230] p-6" data-testid="member-next-appointment-empty">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#68687B]">Sıradaki Randevun</p>
          <p className="font-display font-bold text-lg text-white mt-2">Planlanmış randevun yok</p>
          <button type="button" onClick={() => navigate("/member/appointments")} data-testid="member-book-cta"
            className="mt-4 inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#FF661A] text-white text-sm font-bold px-4 py-2.5 rounded-xl">
            Hemen Randevu Al <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#13131C] border border-[#222230] p-4" data-testid="member-remaining-lessons">
          <div className="w-9 h-9 rounded-xl bg-[#FF5500]/10 flex items-center justify-center mb-3"><Package size={18} className="text-[#FF5500]" /></div>
          <p className="text-xs text-[#68687B]">Kalan Ders</p>
          <p className="font-mono font-extrabold text-2xl text-white mt-0.5">{member.remaining_lessons ?? "—"}<span className="text-sm text-[#68687B]">/{member.total_lessons ?? "—"}</span></p>
          <div className="h-1.5 rounded-full bg-[#1B1B27] mt-2 overflow-hidden"><div className="h-full bg-[#FF5500] rounded-full" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="rounded-2xl bg-[#13131C] border border-[#222230] p-4">
          <div className="w-9 h-9 rounded-xl bg-[#FF5500]/10 flex items-center justify-center mb-3"><CalendarClock size={18} className="text-[#FF5500]" /></div>
          <p className="text-xs text-[#68687B]">Paket Bitiş</p>
          <p className="font-display font-bold text-lg text-white mt-0.5">{fmtDate(member.package_expiry)}</p>
          <p className="text-xs text-[#68687B] mt-1 truncate">{member.package_name || "—"}</p>
        </div>
        <div className="rounded-2xl bg-[#13131C] border border-[#222230] p-4">
          <div className="w-9 h-9 rounded-xl bg-[#FF5500]/10 flex items-center justify-center mb-3"><Flame size={18} className="text-[#FF5500]" /></div>
          <p className="text-xs text-[#68687B]">Bu Hafta</p>
          <p className="font-mono font-extrabold text-2xl text-white mt-0.5">{weekly_workouts}<span className="text-sm text-[#68687B]"> antrenman</span></p>
          <p className="text-xs text-[#68687B] mt-1">tamamlanan</p>
        </div>
        <div className="rounded-2xl bg-[#13131C] border border-[#222230] p-4">
          <div className="w-9 h-9 rounded-xl bg-[#FF5500]/10 flex items-center justify-center mb-3"><User size={18} className="text-[#FF5500]" /></div>
          <p className="text-xs text-[#68687B]">Antrenörüm</p>
          <p className="font-display font-bold text-base text-white mt-0.5 truncate">{member.trainer_name || "—"}</p>
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#68687B] mb-3">Hızlı İşlemler</h3>
        <div className="space-y-2">
          <QuickAction icon={CalendarClock} label="Randevu Al" onClick={() => navigate("/member/appointments")} />
          <QuickAction icon={Package} label="Paketlerim" onClick={() => navigate("/member/packages")} />
          <QuickAction icon={Dumbbell} label="Antrenman Programım" onClick={() => navigate("/member/workout")} />
          <QuickAction icon={Ruler} label="Ölçümlerim" onClick={() => navigate("/member/measurements")} />
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, badge }) {
  return (
    <button onClick={onClick} data-testid={`member-quick-${label}`} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#13131C] border border-[#222230] hover:border-[#FF5500]/40 transition-all">
      <div className="w-10 h-10 rounded-xl bg-[#FF5500]/10 flex items-center justify-center"><Icon size={18} className="text-[#FF5500]" /></div>
      <span className="flex-1 text-left text-sm font-semibold text-white">{label}</span>
      {badge && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">{badge}</span>}
      <ChevronRight size={18} className="text-[#68687B]" />
    </button>
  );
}
